import {
  File,
  Xmark,
  StopFill,
  ArrowLeft,
  ArrowRotateRight,
  ArrowRightToLine,
  CircleInfo,
} from "@gravity-ui/icons";
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiService from "../../api/apiService";
import { useAuth } from "../../contexts/AuthContext";
import { useLocalState } from "../../contexts/LocalStateProvider";
import { getCookie } from "../../utils/cookieUtils";
import Dropdown from "../generic_components/Dropdown";
import Toast from "../generic_components/Toast";
import Modal from "../modal/Modal";
import ModalContent from "../modal/ModalContent";
import { useDispatch, useSelector } from "react-redux";
import {
  setSubscriptionModal,
  setSubscriptionTypeModal,
  setRedirectAfterSelectedPlan,
} from "../../store/reducers/subscriptionSlice";
import { randomPrompts } from "../../utils/randomPrompts";
import TaskCompletion from "./TaskCompletion";
import axios from "axios";
import { useGameContext } from "../../contexts/GameContext";
import * as Sentry from "@sentry/react"
import Groq from 'groq-sdk';
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";

const truncateFileName = (fileName, maxLength) => {
  const extension = fileName.split(".").pop();
  const baseName = fileName.substring(
    0,
    fileName.length - extension.length - 1
  );

  if (baseName.length + extension.length + 1 <= maxLength) {
    return fileName;
  }

  const truncatedBaseName = baseName.substring(
    0,
    maxLength - extension.length - 1
  );
  return `${truncatedBaseName}...${extension}`;
};

// Define allowed file types and extensions
const allowedMimeTypes = new Set([
  // Text
  'text/plain', 'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/rtf',
  'application/vnd.ms-word.template.macroEnabled.12', // .dotm (close to .dot)
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template', // .dotx
  'application/msword', // .dot (older)
  'application/vnd.hancom.hwp', 'application/vnd.hancom.hwpx',
  // Image
  'image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif',
  // Video
  'video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', // .avi
  'video/x-flv', 'video/mpg', 'video/webm',
  // Audio
  'audio/wav', 'audio/mpeg', // .mp3
  'audio/aiff', 'audio/aac', 'audio/ogg', 'audio/flac',
]);

const allowedExtensions = new Set([
  // Text
  '.txt', '.pdf', '.doc', '.docx', '.rtf', '.dot', '.dotx', '.hwp', '.hwpx',
  // Image
  '.png', '.jpeg', '.jpg', '.webp', '.heic', '.heif',
  // Video
  '.mp4', '.mpeg', '.mov', '.avi', '.flv', '.mpg', '.webm',
  // Audio
  '.wav', '.mp3', '.aiff', '.aac', '.ogg', '.flac',
]);

// Helper function to check if text contains only printable characters (supports all languages)
const isValidText = (text) => {
  // Allow all printable characters and common whitespace/control characters
  // This supports all languages while filtering out only problematic control characters
  const invalidCharRegex = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/;
  return !invalidCharRegex.test(text);
};

// Helper function to validate file type
const isFileTypeAllowed = (file) => {
  if (!file) return false;
  // Getting extension might fail if file name has no dot, handle defensively
  const nameParts = file.name.split('.');
  const fileExtension = nameParts.length > 1 ? '.' + nameParts.pop().toLowerCase() : '';
  // Check if either MIME type or extension is in the allowed lists
  return allowedMimeTypes.has(file.type?.toLowerCase()) || allowedExtensions.has(fileExtension);
};

const CustomInput = ({
  className,
  dropdownStyle,
  closeModal,
  audioFile,
  requestData,
  project_id,
  chatPage,
  imageSrc,
  mood,
  onRedo,
  fromDiscoverPage,
  id,
}) => {
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const { localState, updateLocalState } = useLocalState();
  const [modalContent, setModalContent] = useState(null);
  const [file, setFile] = useState(null);
  const [textPrompt, setTextPrompt] = useState(localState.textPrompt || "");
  const [filePreview, setFilePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("info");
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const maxHeight = 90;
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [inChatPage, setInChatPage] = useState(chatPage);
  const { isLoggedIn } = useAuth();
  const [diceIsClicked, setDiceIsClicked] = useState(false);
  const [lastPromptIndex, setLastPromptIndex] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [shouldGenerate, setShouldGenerate] = useState(false);
  const [disableGenerate, setDisableGenerate] = useState(false);
  const dropRef = useRef(null);
  const [charCount, setCharCount] = useState(0);
  const [userData, setUserData] = useState();
  const MAX_CHARS = 2000;
  const [showSuggestion, setShowSuggestion] = useState(true);
  const [triggerTaskModal, setTriggerTaskModal] = useState(false);
  const { fetchGamificationData, gamificationData } = useGameContext();
  const [showExtendTooltip, setShowExtendTooltip] = useState(false);
  const [fileType, setFileType] = useState(null);

  const [userCreditsRemaining, setUserCreditsRemaining] = useState(
    localState?.userProfileData?.remaining_credit
  );

  const [suggestions, setSuggestions] = useState([]);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [showSuggestionsContainer, setShowSuggestionsContainer] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const ignoreNextPromptEffectRef = useRef(false); // ADD
  const suggestionsContainerRef = useRef(null);
  const mainInputBoxRef = useRef(null); // Ref for the main input box

  const isHomepageInput = id === "search-input-homepage";
  const isCreateNewModalInput =
    requestData?.createProject === true &&
    !!closeModal &&
    !chatPage &&
    !fromDiscoverPage &&
    !onRedo;
  const shouldShowAutocomplete = isHomepageInput; // NEW Condition: Only on homepage

  const groq = new Groq({ apiKey: process.env.REACT_APP_GROQ_API_KEY, dangerouslyAllowBrowser: true });
  const debounceTimeoutRef = useRef(null);

  const [textareaHeight, setTextareaHeight] = useState(0);

  const fetchGroqSuggestions = async (promptText) => {
    if (!promptText.trim() || !groq) return;
    
    // Check if any existing suggestion matches the current input
    const hasMatchingSuggestion = suggestions.some(suggestion => 
      suggestion.toLowerCase().startsWith(promptText.toLowerCase())
    );
    
    // If we have matching suggestions, don't fetch new ones
    if (hasMatchingSuggestion) {
      return;
    }

    setIsFetchingSuggestions(true);
    setSuggestions([]); 

    try {
      const chatCompletion = await groq.chat.completions.create({
        messages: [
          {
            role: "system",
            content: "You are an AI assistant specializing in generating auto-completion suggestions for instrumental music prompts. Your primary task is to take the user\'s partial text input and provide several diverse and relevant ways to complete it, thereby forming a list of complete, actionable music creation prompts.\n\nWhen the user types, analyze their input and offer a few distinct auto-completion suggestions. Each of these suggestions must adhere to the following criteria:\n\nNatural Continuation: It must be a logical and coherent completion of the user\'s current text.\nConciseness: The entire auto-completed prompt (user\'s input + your completion) must be a maximum of 10 words.\nInstrumental Focus: The completed prompt should clearly describe or request royalty-free instrumental music (no vocals).\nCommercial Use Case Suitability: The music described should be appropriate for at least one of the following commercial use cases:\nReels\nAdvertisements\nGaming\nFilms\nPodcasts\nVlogs\nPresentations\nWellness (e.g., meditation, spa music)\nRetail (e.g., in-store ambiance)\nEvents (e.g., background music for conferences, parties)\nUser-Friendliness: The language of the completed prompt must be simple, clear, and sound natural.\nDiversity in Suggestions: For any given user input, the multiple auto-completion suggestions you provide should showcase variety. This means suggesting completions that might lead to different moods, styles, tempos, or target specific use cases, all while accurately extending the user\'s original partial input.\nYour overall goal: To help the user quickly and easily formulate a precise and effective prompt for music generation by intelligently showing them plausible ways their initial thought or phrase can be completed.\n\nExample Interaction:\n\nIf the user types: \"dark cinematic\"\n\nYou might suggest the following auto-completions:\n\n\"dark cinematic trailer music for film\"\n\"dark cinematic underscore for game mystery\"\n\"dark cinematic ambient for podcast series\"\n\"dark cinematic tension for a vlog\"\nIf the user types: \"a track for\"\n\nYou might suggest:\n\n\"a track for upbeat travel Reels\"\n\"a track for focused wellness sessions\"\n\"a track for intense gaming moments\"\n\"a track for corporate presentation intros\"\n\nStrictly provide only the auto-completion suggestions, each on a new line. Do not include any other text, greetings, or explanations.",
          },
          {
            role: "user",
            content: `Given the user input \"${promptText}\", provide autocomplete suggestions as per the criteria.`, // Refined user message
          },
        ],
        model: "gemma2-9b-it", 
        temperature: 0.7, 
        max_tokens: 100,   
        top_p: 1,
        stream: false,     
      });

      const rawContent = chatCompletion.choices[0]?.message?.content;

      const initialParsedSuggestions = rawContent
        ?.split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0) || [];

      const newSuggestions = initialParsedSuggestions
        .filter(s => s.toLowerCase().startsWith(promptText.toLowerCase()))
        .slice(0, 3);
      
      setSuggestions(newSuggestions);
      setShowSuggestionsContainer(newSuggestions.length > 0);
      setActiveSuggestionIndex(-1);

    } catch (error) {
      console.error("[CustomInput] Error fetching suggestions from Groq:", error);
      setSuggestions([]); 
      setShowSuggestionsContainer(false);
    } finally {
      setIsFetchingSuggestions(false);
    }
  };

  useEffect(() => {
    // If the textPrompt was just changed by a suggestion click, ignore this effect run.
    if (ignoreNextPromptEffectRef.current) {
      ignoreNextPromptEffectRef.current = false; // Reset the flag for next time
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Ensure suggestions stay hidden if they were hidden by the click handler
      // setShowSuggestionsContainer(false); // Usually already false from click handler
      // setSuggestions([]);              // Usually already empty from click handler
      // setIsFetchingSuggestions(false); // Should be false
      return; // Stop processing this effect run
    }

    if (shouldShowAutocomplete && textPrompt.trim().length > 0) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Show loading immediately when user types, but fetch is debounced
      // setIsFetchingSuggestions(true); 
      // setShowSuggestionsContainer(true); // Keep suggestions container open for loading indicator
      // setSuggestions([]); // Clear old suggestions

      debounceTimeoutRef.current = setTimeout(() => {
        fetchGroqSuggestions(textPrompt);
      }, 500); // 500ms debounce
    } else if (shouldShowAutocomplete) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      setSuggestions([]);
      setShowSuggestionsContainer(false); // Ensure it's hidden before debounced fetch
      setActiveSuggestionIndex(-1);
      setIsFetchingSuggestions(false);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [textPrompt, shouldShowAutocomplete]); // REMOVE lastClickedSuggestion from dependencies

  // Click outside handler for suggestions
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showSuggestionsContainer &&
        suggestionsContainerRef.current &&
        !suggestionsContainerRef.current.contains(event.target) &&
        mainInputBoxRef.current &&
        !mainInputBoxRef.current.contains(event.target)
      ) {
        setShowSuggestionsContainer(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestionsContainer, suggestionsContainerRef, mainInputBoxRef]);

  const handleSuggestionClick = (suggestionText) => {
    if (debounceTimeoutRef.current) { 
      clearTimeout(debounceTimeoutRef.current);
    }
    // setLastClickedSuggestion(suggestionText); // REMOVE
    ignoreNextPromptEffectRef.current = true; // SET FLAG HERE
    setTextPrompt(suggestionText);
    setShowSuggestionsContainer(false);
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.setSelectionRange(
            suggestionText.length,
            suggestionText.length
          );
        }
      }, 0);
    }
    posthog.capture("Autocomplete suggestion selected", {
      suggestion: suggestionText,
      context: isHomepageInput ? "homepage" : "create_new_modal",
      is_user_logged_in: true
    });
  };

  const handleTextareaKeyDown = (e) => {
    if (shouldShowAutocomplete && showSuggestionsContainer && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) =>
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
        );
      } else if (e.key === "Enter") {
        if (activeSuggestionIndex > -1 && activeSuggestionIndex < suggestions.length) {
          e.preventDefault();
          handleSuggestionClick(suggestions[activeSuggestionIndex]);
        } else if (textPrompt?.trim() && !isInputDisabled) {
          e.preventDefault();
          handleGenerate();
          posthog.capture("Keyboard-Key-Pressed-CustomInputBox-With-Generate",{
            is_user_logged_in: true
          });
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestionsContainer(false);
        setActiveSuggestionIndex(-1);
      }
    } else if (e.key === "Enter" && textPrompt?.trim() && !isInputDisabled) {
      e.preventDefault();
      handleGenerate();
      posthog.capture("Keyboard-Key-Pressed-CustomInputBox-Default-Generate", {
        is_user_logged_in: true
      });
    }
  };

  useEffect(
    () =>
      setUserCreditsRemaining(localState?.userProfileData?.remaining_credit),
    [localState?.userProfileData?.remaining_credit]
  );  const [isDragging, setIsDragging] = useState(false);
  const [dragEventSent, setDragEventSent] = useState(false);
  // Track the current drag session with a unique ID
  const dragSessionRef = useRef(null);

  const lastMessageInHistory =
    localState.messages && localState.messages.length > 0
      ? localState.messages[localState.messages.length - 1]
      : null;

  const isLastMessageAnAiError =
    lastMessageInHistory &&
    lastMessageInHistory.source === 'ai' &&
    lastMessageInHistory.is_error === true;

  const isLastMessageAiTextResponse =
    lastMessageInHistory &&
    lastMessageInHistory.source === 'ai' &&
    lastMessageInHistory.responseType === 'text' &&
    !lastMessageInHistory.is_error;

  const isDisabledByTrackSelectionRequirement =
    chatPage &&
    !!localState.chatInputRequiresTrackSelectionForId &&
    !isLastMessageAnAiError &&
    !isLastMessageAiTextResponse;

  const isInputDisabled = (chatPage && (localState.responseInProgress || isDisabledByTrackSelectionRequirement)) || localState.responseInProgress;


  const showToast =
    toastMessage === "File size exceeds the 20 MB limit." ||
    toastMessage === "Please enter some text." ||
    toastMessage === "Please wait for the current response to finish." ||
    toastMessage === "Maximum character limit reached." ||
    toastMessage === "Please enter text in English only." ||
    toastMessage === "Please paste text in English only." ||
    toastMessage === "Selected Type isnt uploaded";

  useEffect(() => {
    if (chatPage) {
      //console.log("[CustomInput] chatInputRequiresTrackSelectionForId:", localState.chatInputRequiresTrackSelectionForId, "isDisabledByTrackSelectionRequirement:", isDisabledByTrackSelectionRequirement);
    }
  }, [localState.chatInputRequiresTrackSelectionForId, chatPage, isDisabledByTrackSelectionRequirement]);

  const taskUpdate = async () => {
    if (gamificationData.tasks[6]?.is_completed === false) {
      const token = getCookie("token") || getPartitionedLocalStorage("token");
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/complete-task`,
        { taskKey: "use_smart_assist" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      //console.log(response.data)

      if (response.data.status === "completed") {
        setTriggerTaskModal(true);
        setTimeout(() => setTriggerTaskModal(false), 8000);
        await fetchGamificationData();
      }
    }
  }

  const handleDiceClick = () => {
    posthog.capture("Click-Randomize-CustomInputBox", {
      is_user_logged_in: true
    })
    setDiceIsClicked(true);


    if (textareaRef.current) {
      textareaRef.current.focus();
    }

    posthog.capture("Click on Randomize Prompt Icon Button", {
      is_user_logged_in: true
    });

    // Randomly select an index different from the last one
    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * randomPrompts.length);
    } while (randomIndex === lastPromptIndex); // Ensure new index is different from the last

    // Update state with the random prompt
    const randomPrompt = randomPrompts[randomIndex];
    setTextPrompt(randomPrompt);
    setCharCount(randomPrompt.length);
    setLastPromptIndex(randomIndex); // Update last prompt index state

    // make an api call

    // Reset the clicked state after animation (0.3s for example)
    setTimeout(() => {
      setDiceIsClicked(false);
    }, 300); // Adjust duration as needed
  };

  const location = useLocation();

  useEffect(() => {
    // updateLocalState("textPrompt", ""); // REMOVED: Don't clear localState here
    // updateLocalState("file", null); // REMOVED: Don't clear localState here

    // Reset component's internal state on navigation only if NOT on chatPage
    if (location.pathname !== "/chatPage") {
      setTextPrompt("");
      setFile(null);
      updateLocalState("nextPromptSuggestion", null);
    }
    // DO NOT clear localState.textPrompt or localState.file here
  }, [location.pathname]); // Removed updateLocalState dependency

  useEffect(() => { }, [textPrompt, chatPage, file]);

  // Fetch user data once when the provider mounts
  // useEffect(() => {
  //   const fetchUserData = async () => {
  //     try {
  //       const response = await apiService.handleProtectedRoute(
  //         "fetchUserProfile",
  //         {}
  //       );

  //       updateLocalState("userProfileData", response);
  //     } catch (error) {
  //       console.error("Error fetching user data:", error);
  //     }
  //   };

  //   if (isLoggedIn) {
  //     fetchUserData();
  //   }
  // }, []);

  // Add the circular progress component
  const CircularProgress = ({ value, maxChars }) => {
    const radius = 8; // Radius of the circle
    const circumference = 2 * Math.PI * radius;

    // Calculate progress percentage and cap it at 100%
    const progress = Math.min((value / maxChars) * 100, 100);

    // Compute stroke offset based on capped progress
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className="relative flex items-center justify-center">
        <span className="text-xs font-extralight text-[#C5C3C6]">
          {Math.min(value, maxChars)}
        </span>
        <svg className="w-8 h-8 transform -rotate-90">
          <circle
            className="text-gray-700"
            strokeWidth="2"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx="16"
            cy="13"
          />
          <circle
            className={`${value >= maxChars ? "text-red-500" : "text-primary"}`}
            strokeWidth="2"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx="16"
            cy="13"
          />
        </svg>
      </div>
    );
  };
  const handleChange = (e) => {
    let inputValue = e.target.value; // Create a copy of the input value

    // Get the previous input and check only what's changed
    const previousInput = textPrompt;
    const isNewInput = inputValue.length > previousInput.length;

    // If there's new input, validate only the new characters for problematic control characters
    if (isNewInput) {
      const newCharacters = inputValue.substring(previousInput.length);
      if (!isValidText(newCharacters)) {
        posthog.capture("Invalid text input attempt", {
          is_user_logged_in: true
        });
        setToastMessage("Please enter valid text characters only.");
        setToastType("error");
        // Don't update the input - keep the previous value
        e.target.value = previousInput;
        return;
      }
    }

    setCharCount(inputValue.length);
    const wordCount = inputValue.trim().split(/\s+/).length;

    // Character limit
    const charLimit = 2000;

    // Enforce character limit and truncate pasted input
    if (
      (inputValue.length > charLimit && userData?.remaining_credits !== null) ||
      userData?.remaining_credits === 0
    ) {
      setDisableGenerate(true); // Disable the generate button
      if (inputValue.length > charLimit) {
        setToastMessage("Maximum character limit exceeded."); // Display toast message
        setToastType("error"); // Set toast type to error
      }
    } else {
      setDisableGenerate(false); // Enable the generate button
    }

    setTextPrompt(inputValue);

    // Capture the event for typing in the music creation prompt
    if (inputValue?.trim()) {
      posthog.capture("Typing in Music Creation Prompt", {
        is_user_logged_in: true
      });
    }
    // Calculate the number of lines
    const lines = inputValue.split("\n").filter((line) => line.trim() !== "");

    // Adjust the textarea height dynamically
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        100
      )}px`;
      textareaRef.current.style.overflow =
        textareaRef.current.scrollHeight > 100 ? "auto" : "hidden";
    }
  };

  useEffect(() => {
    const handlePaste = (e) => {
      // Get pasted content
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData('text');

      if (!isValidText(pastedText)) {
        e.preventDefault();
        posthog.capture("Invalid text paste attempt", {
          is_user_logged_in: true
        });
        setToastMessage("Please paste valid text characters only.");
        setToastType("error");
      }
    };

    // Add paste event listener to textarea
    if (textareaRef.current) {
      textareaRef.current.addEventListener('paste', handlePaste);
    }

    // Cleanup
    return () => {
      if (textareaRef.current) {
        textareaRef.current.removeEventListener('paste', handlePaste);
      }
    };
  }, [textareaRef]);

  useEffect(() => {
    // Focus the textarea when the component mounts
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []); //

  useEffect(() => {
    // Calculate the number of lines
    const lines = textPrompt?.split("\n").filter((line) => line.trim() !== "");

    // Adjust the textarea height dynamically
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        100
      )}px`;
      textareaRef.current.style.overflow =
        textareaRef.current.scrollHeight > 80 ? "auto" : "hidden";
    }

    chatPage &&
      textareaRef.current.scrollHeight <= 98 &&
      updateLocalState("textInputHeight", textareaRef.current.scrollHeight);
  }, [textPrompt, chatPage, file]);

  useEffect(() => {
    if (file && file instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
        setIsUploading(false);
      };
      setIsUploading(true);
      reader.readAsDataURL(file.file);
    } else {
      setFilePreview(null);
    }
  }, [file]);

  useEffect(() => {
    if (audioFile) {
      const fileData = {
        name: audioFile.name,
        type: "audio/wav",
        url: audioFile.url,
      };
      setFile(fileData);
    }
  }, [audioFile]);
  const handleOptionSelect = (option) => {
    console.log("--- handleOptionSelect Start ---");
    console.log("Option received :", option);
    console.log("Is chatPage:", chatPage);

    setFileType(option?.toLowerCase() ?? '');


    // Check if input is disabled or if a response is in progress
    if (isInputDisabled || localState.responseInProgress) {
      console.log("Input is disabled or response in progress - ignoring option selection");
      setToastMessage("Please wait for the current process to complete.");
      setToastType("warning");
      return;
    }

    // Determine the action based on the received option type and value
    let actionType = null;
    if (typeof option === 'string') {
      // For chat page: 'Attach', 'Edit', or file type strings like 'Document', 'Audio', etc.
      actionType = option;
    } else if (typeof option === 'object' && option !== null) {
      // Check if it's the special Extend option with JSX content, or a regular option object
      if (option.label && typeof option.label === 'object') {
        actionType = 'Extend'; // JSX label means it's the Extend option
      } else {
        actionType = option.label || option; // Regular option object
      }
    }
    console.log("Determined actionType:", actionType);

    // Log PostHog based on determined action
    if (actionType) {
      posthog.capture(`Click on Dropdown action ${actionType}`, {
        is_user_logged_in: true
      });
    }

    if (chatPage) {
      console.log("[Chat Page Logic]");
      if (actionType === 'Extend' || actionType === 'Edit') {
        if (actionType === 'Extend') {
          posthog.capture("Click-Extend-CustomInputBox", {
            is_user_logged_in: true
          });
        } else {
          [posthog.capture("Click-Edit-CustomInputBox", {
            is_user_logged_in: true
          })];
        }
        console.log("-> Matched Extend/Edit");
        // Handle Extend and Edit: Set text prompt
        const prefix = `${actionType} `;
        console.log("   Setting text prompt prefix:", prefix);
        setTextPrompt(prefix);
        setCharCount(prefix.length);
        setTimeout(() => {
          if (textareaRef.current) {
            console.log("   Focusing textarea and setting cursor");
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(prefix.length, prefix.length);
          } else {
            console.log("   Textarea ref not found for focus");
          }
        }, 0);
        // Don't proceed further for Extend/Edit on chat page
        console.log("--- handleOptionSelect End (Extend/Edit) ---");
        return;
      } else if (actionType === 'Attach') {
        console.log("-> Matched Attach");
        posthog.capture("Click-Attach-CustomInputBox", {
          is_user_logged_in: true
        });
        // Handle Attach on chat page: Trigger generic file input
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        console.log("   Created file input element");
        // You might want to add specific accept attributes here if needed for Attach
        fileInput.onchange = (event) => {
          console.log("   File input onChange triggered");
          handleFileChange(event);
          posthog.capture("Click-UploadContent-CustomPromptModal", {
            fileName: event.target.files?.[0]?.name,
            fileType: event.target.files?.[0]?.type,
            is_user_logged_in: true
          });
        };
        try {
          console.log("   Attempting to click file input programmatically");
          fileInput.click();
          console.log("   File input clicked successfully");
        } catch (error) {
          console.error("   Error clicking file input:", error);
          Sentry.captureException(error);
        }
        // Don't proceed further for Attach on chat page
        console.log("--- handleOptionSelect End (Attach) ---");
        return;
      }
      console.log("-> No match for actionType in chat page logic");
      // No default action for other potential options on chat page
    } else {
      console.log("[Non-Chat Page Logic]");
      // Only process actual file type selections, not chat actions
      const validFileTypes = ['Document', 'Audio', 'Image', 'Video'];
      if (!validFileTypes.includes(actionType)) {
        console.log("Not a valid file type for non-chat page, ignoring:", actionType);
        return;
      }

      // Original logic for non-chat pages (e.g., Upload Audio/Image/Video/Document)
      setModalContent(option);
      console.log("  Set modal content");

      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      console.log("   Created file input element");

      // Set accept attribute based on the selected option label (original logic)
      // Handle both string and object option formats
      const label = typeof option === 'string' ? option : (typeof option.label === 'string' ? option.label : '');
      console.log("   Option label for accept attribute:", label);
      // Set accept attributes for better filtering in the file dialog
      if (label === 'Audio') {
        fileInput.accept = '.wav,.mp3,.aiff,.aac,.ogg,.flac';
        posthog.capture("Click-Audio-CustomInputBox", {
          is_user_logged_in: true
        });
      } else if (label === 'Image') {
        fileInput.accept = '.png,.jpg,.jpeg,.webp,.heic,.heif';
        posthog.capture("Click-Image-CustomInputBox", {
          is_user_logged_in: true
        });
      } else if (label === 'Video') {
        fileInput.accept = '.mp4,.mov,.avi,.mpeg,.flv,.mpg,.webm';
        posthog.capture("Click-Video-CustomInputBox", {
          is_user_logged_in: true
        });
      } else if (label === 'Document') {
        fileInput.accept = '.txt,.pdf,.doc,.docx,.rtf,.dot,.dotx,.hwp,.hwpx';
        posthog.capture("Click-Document-CustomInputBox", {
          is_user_logged_in: true
        });
      }

      fileInput.onchange = (event) => {
        console.log("   File input onChange triggered");
        handleFileChange(event);
        posthog.capture("Click-UploadContent-CustomPromptModal", {
          is_user_logged_in: true,
          fileName: event.target.files?.[0]?.name,
          fileType: event.target.files?.[0]?.type,
        });
      };
      try {
        console.log("   Attempting to click file input programmatically");
        fileInput.click();
        console.log("   File input clicked successfully");
      } catch (error) {
        console.error("   Error clicking file input:", error);
        Sentry.captureException(error);
      }
    }
    console.log("--- handleOptionSelect End (Default Fallthrough) ---");
  };

  const MAX_FILE_SIZE_MB = 20; // Maximum file size in MB
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // Convert to bytes

  const taskData = async (fileType) => {
    const acceptedTypes = {
      audio: [
        'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/webm',
        'audio/ogg', 'audio/mp4', 'audio/aac'
      ],
      video: [
        'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime',
        'video/x-msvideo', 'video/x-ms-wmv', 'video/avi', 'video/mpeg'
      ],
      image: [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
        'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff',
        'image/heic', 'image/heif'
      ],
    };

    try {
      const token = getCookie("token") || getPartitionedLocalStorage("token");

      // Check Video Task
      if (acceptedTypes.video.includes(fileType) && gamificationData.tasks[3]?.is_completed === false) {
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/complete-task`,
          { taskKey: "generate_video_prompt" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === "completed") {
          console.log("Video prompt task completed");
          setTriggerTaskModal(true);
          setTimeout(() => setTriggerTaskModal(false), 8000);
          await fetchGamificationData();
        }
      }

      // Check Image Task
      if (acceptedTypes.image.includes(fileType) && gamificationData.tasks[4]?.is_completed === false) {
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/complete-task`,
          { taskKey: "generate_image_prompt" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === "completed") {
          console.log("Image prompt task completed");
          setTriggerTaskModal(true);
          setTimeout(() => setTriggerTaskModal(false), 8000);
          await fetchGamificationData();
        }
      }

    } catch (error) {
      console.error("Error on Data uploading:", error);
      Sentry.captureException(error);
    }
  };

  // OLD APPROACH
  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    taskData(selectedFile.type);
    if (selectedFile) {
      // *** Add file type validation ***
      if (!isFileTypeAllowed(selectedFile)) {
        posthog.capture("Invalid file type upload attempt", {is_user_logged_in: true, fileName: selectedFile.name, fileType: selectedFile.type });
        setToastMessage("Invalid file type. Please upload a supported format.");
        setToastType("error");
        setIsUploading(false);
        setModalContent(null); // Close modal if open
        return; // Stop processing
      }
      // *** End file type validation ***

      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        posthog.capture("File size exceeds the 20 MB limit. Upload failed", {
          is_user_logged_in: true
        });
        // File size exceeds the limit
        setToastMessage("File size exceeds the 20 MB limit.");
        setToastType("error");
        setIsUploading(false);
        return;
      }

      setModalContent(null);
      setFileType(null)

      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("folder", "user-uploads");

      try {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        setFile({
          name: selectedFile.name,
          type: selectedFile.type,
          file: selectedFile,
          url: data.url,
        });

        posthog.capture("File uploaded successfully", {
          is_user_logged_in: true
        });
      } catch (error) {
        posthog.capture("Error uploading file", {
          is_user_logged_in: true
        });
        console.error("Error uploading file:", error);
        Sentry.captureException(error);
        setToastMessage("Failed to upload file");
        setToastType("error");
      }

      // fromDiscoverPage
      //   ? setFile(selectedFile)
      //   : setFile({
      //       name: selectedFile.name,
      //       type: selectedFile.type,
      //       file: selectedFile,
      //     });
      setIsUploading(false);
    }
    setModalContent(null);
  };

  // NEW APPROACH
  // const handleFileChange = async (event) => {
  //   const selectedFile = event.target.files[0];
  //   taskData(selectedFile?.type);

  //   if (!selectedFile) {
  //     console.log("No file selected");
  //     return;
  //   }

  //   if (!isFileTypeAllowed(selectedFile)) {
  //     posthog.capture("Invalid file type upload attempt", {
  //       fileName: selectedFile.name,
  //       fileType: selectedFile.type
  //     });
  //     setToastMessage("Invalid file type. Please upload a supported format.");
  //     setToastType("error");
  //     setIsUploading(false);
  //     return;
  //   }

  //   if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
  //     posthog.capture("File size exceeds the 20 MB limit. Upload failed");
  //     setToastMessage("File size exceeds the 20 MB limit.");
  //     setToastType("error");
  //     setIsUploading(false);
  //     return;
  //   }

  //   setIsUploading(true);

  //   try {
  //     // Step 1: Get pre-signed URL from backend
  //     const presignResponse = await axios.post(
  //       `${process.env.REACT_APP_SERVER_ENDPOINT}/api/generate-presigned-url`,
  //       {
  //         fileName: (selectedFile.name),
  //         fileType: selectedFile.type,
  //       }
  //     );

  //     console.log(selectedFile.type)

  //     const { signedUrl, publicUrl } = presignResponse.data;

  //     console.log("Signed URL:", signedUrl);
  //     console.log("Public URL:", publicUrl);

  //     if (!signedUrl) {
  //       console.error("No signed URL returned from backend");
  //       throw new Error("Missing signed URL");
  //     }

  //     // Step 2: Upload file to GCS
  //     const uploadResponse = await axios.post(
  //       signedUrl,
  //       selectedFile,
  //       {
  //         headers: {
  //           'Content-Type': 'application/octet-stream',
  //         }
  //       }
  //     );

  //     if (uploadResponse.status === 200) {
  //       setFile({
  //         name: selectedFile.name,
  //         type: selectedFile.type,
  //         file: selectedFile,
  //         url: publicUrl
  //       });

  //       posthog.capture("File uploaded successfully via presigned URL");
  //     } else {
  //       throw new Error(`Upload failed with status ${uploadResponse.status}`);
  //     }
  //   } catch (error) {
  //     console.error("Error in file upload process:", error?.response?.data || error.message || error);
  //     posthog.capture("Error uploading file via presigned URL");
  //     setToastMessage("Failed to upload file");
  //     setToastType("error");
  //   } finally {
  //     setIsUploading(false);
  //     setModalContent(null);
  //   }
  // };



  const handleFileRemove = () => {
    setFile(null);
    posthog.capture("Click-DeleteFileUpload-CustomInputBox", {
      is_user_logged_in: true
    })
  };  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    setDragEventSent(false); // Reset the flag when drag operation ends
    dragSessionRef.current = null; // Reset the drag session reference
    const droppedFile = e.dataTransfer.files[0];

    // *** Update file type check using the helper function ***
    if (droppedFile && isFileTypeAllowed(droppedFile)) {
      // Create file object matching the structure expected by handleFileChange
      const fileEvent = {
        target: {
          files: [droppedFile],
        },
      };
      handleFileChange(fileEvent); // This will also check size limit
    } else {
      // Show error for invalid type or if no file was dropped
      const message = droppedFile
        ? "Invalid file type. Please upload a supported format."
        : "No file detected.";
      posthog.capture("Invalid file type drop attempt", { is_user_logged_in: true,fileName: droppedFile?.name, fileType: droppedFile?.type });
      setToastMessage(message);
      setToastType("error");
    }
    // *** End updated file type check ***
  };  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    
    // Only send the event once per drag operation
    if (!dragSessionRef.current) {
      // Create a unique session ID for this drag operation
      dragSessionRef.current = Date.now().toString();
      // Send the PostHog event with the drag session ID
      posthog.capture("Dragging file over the drop zone", { 
        is_user_logged_in: true,
        dragSessionId: dragSessionRef.current 
      });
    }
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if the drag event is leaving the entire drop zone
    if (!dropRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
      // Don't reset dragSessionRef here, as the user might re-enter the drop zone
      // We only want to reset it on actual drop or when drag ends completely
    }
  };

  // Update textPrompt when localState.textPrompt changes
  useEffect(() => {
    if (localState.textPrompt !== undefined) {
      setTextPrompt(localState.textPrompt);
    }
  }, [localState.textPrompt]);

  // Preserve textPrompt to localStorage when user is not logged in and has text
  useEffect(() => {
    if (!isLoggedIn && textPrompt.trim()) {
      localStorage.setItem("tempTextPrompt", textPrompt);
    } else if (!isLoggedIn && !textPrompt.trim()) {
      localStorage.removeItem("tempTextPrompt");
    }
  }, [isLoggedIn, textPrompt]);

  // Restore textPrompt from localStorage when user signs in
  useEffect(() => {
    if (isLoggedIn) {
      const savedPrompt = localStorage.getItem("tempTextPrompt");
      if (savedPrompt && savedPrompt.trim() && !textPrompt) {
        setTextPrompt(savedPrompt);
        setCharCount(savedPrompt.length);
        // Clean up after restoration
        localStorage.removeItem("tempTextPrompt");
      }
    }
  }, [isLoggedIn]);

  // Add effect to reset showSuggestion when new suggestion comes in
  useEffect(() => {
    if (localState.nextPromptSuggestion) {
      setShowSuggestion(true);
    }
  }, [localState.nextPromptSuggestion]);

  // Update file when localState.file changes
  useEffect(() => {
    // Restore file from localState if it exists
    if (localState.file) {
      setFile(localState.file);
    }
    // Don't clear localState.file here, handleGenerate will clear it after submission.
  }, [localState.file]);

  useEffect(() => {
    if (localState.suggestionClicked) {
      if (localState?.userProfileData?.remaining_credits <= 0) {
        if (localState.isAdmin && localState.isTeamMember) {
          dispatch(setRedirectAfterSelectedPlan("/create-team"));
          dispatch(setSubscriptionModal(true));
          dispatch(setSubscriptionTypeModal("upgrade"));
        } else if (!localState.isTeamMember) {
          dispatch(setRedirectAfterSelectedPlan("/create-team"));
          dispatch(setSubscriptionModal(true));
        }
        return;
      }

      const { text, file } = localState.suggestionClicked;
      setTextPrompt(text);

      const processFile = async () => {
        let newFile = null;
        setIsUploading(true);
        if (file) {
          newFile = {
            name: file.name,
            type: file.type,
            file: file.file,
            url: file.url,
          };
          setFile(newFile);
          await handleGenerate(newFile);
        }
        setFile(newFile);
        setIsUploading(false);
      };

      file ? processFile() : text && handleGenerate();
      updateLocalState("suggestionClicked", null);
    }
  }, [localState.suggestionClicked]);

  const handleStopGeneration = async () => {
    updateLocalState("stopGeneration", true);
    updateLocalState("responseInProgress", false);
    console.log("Stop generation clicked", localState.stopGeneration, localState.responseInProgress, localState);
    posthog.capture("Click Stop generation icon button", {
      is_user_logged_in: true
    });
  };
  const handleGenerate = async (suggestionFile) => {
    const currentFile =
      suggestionFile && suggestionFile.file ? suggestionFile : file;

    const prompt = localState.suggestionClicked
      ? localState.suggestionClicked?.text?.trim()
      : textPrompt?.trim();

    // Check if user is logged in FIRST, before any processing begins
    if (!isLoggedIn) {
      updateLocalState("textPrompt", prompt);
      updateLocalState("file", currentFile);
      updateLocalState("showLoginModal", (prev) => !prev);
      return;
    }

    // Now that we know user is logged in, we can start the generation process
    updateLocalState("responseInProgress", true);
    updateLocalState("stopGeneration", false);

    // Capture the event for clicking the Generate button
    posthog.capture("Click on Generate Icon Button", {
      is_user_logged_in: true
    });

    const formData = new FormData();

    // Validate currentFile.url before proceeding
    if (currentFile && currentFile.url && !currentFile.url.startsWith('gs://')) {
      console.error(
        "Invalid file.url for AI processing. Expected a GCS URI (gs://...). Received:",
        currentFile.url
      );
      setToastMessage(
        "Error: File path is not a valid GCS URI. Please re-upload or check simulated file handling."
      );
      setToastType("error");
      setIsUploading(false); // Ensure loading state is reset
      updateLocalState("responseInProgress", false); // Reset progress state
      return; // Stop further processing
    }    if (localState?.userProfileData?.remaining_credits <= 0) {
      if (localState.isAdmin && localState.isTeamMember) {
        dispatch(setRedirectAfterSelectedPlan("/create-team"));
        dispatch(setSubscriptionModal(true));
        dispatch(setSubscriptionTypeModal("upgrade"));
      } else if (!localState.isTeamMember) {
        dispatch(setRedirectAfterSelectedPlan("/create-team"));
        dispatch(setSubscriptionModal(true));
      }
      updateLocalState("responseInProgress", false); // Reset progress state
      return;
    }

    if (localState.responseInProgress) {
      setToastMessage("Please wait for the current response to finish.");
      setToastType("warning");
      updateLocalState("responseInProgress", false); // Reset progress state
      return;
    }

    if (!prompt?.trim() && currentFile && fromDiscoverPage) {
      setToastMessage("Please enter some text.");
      setToastType("error");
      return; // Exit the function
    }

    if (prompt?.trim() || currentFile) {
      const userId = getCookie("user_id") || getPartitionedLocalStorage("user_id");

      try {
        let createProject = requestData?.createProject;
        let projectId;

        if (createProject) {
          // Case where a new project needs to be created
           const now = new Date();

           const pad = (n) => n.toString().padStart(2, '0');

           const hh = pad(now.getHours());       
           const mm = pad(now.getMinutes());    
           const ss = pad(now.getSeconds());    
           const rand = Math.floor(Math.random() * 100 );
           const randPart = pad(rand);

          //  const timePart = `${hh}${mm}${ss}${randPart}`;   
           const timePart = `${hh}${mm}${ss}`;   
           const userPart = userId % 1000;       


           const project_id = Number(`${userPart}${timePart}`); // e.g., 345134506
          //  console.log("PROJECT ID : " ,  project_id)
          const projectPayload = {
            project_id:project_id,
            user_id: userId,
            project_name: "New Project",
            project_description: prompt || "Project Description",
          };

          const createProjectResponse = await apiService.sendRequest(
            "createProject",
            projectPayload
          );
          if (createProjectResponse.error) {
            throw new Error(createProjectResponse.error);
          }

          projectId = createProjectResponse.project.project_id;

          setToastMessage("Project created successfully!");
          setToastType("success");
        } else {
          // Case where the project already exists and is passed as a prop
          projectId = project_id;
        }

        formData.append("project_id", projectId);
        formData.append("user_id", userId);
        formData.append("content", prompt);
        formData.append("sentAt", new Date().toISOString());

        let key = null;
        let fileContent = null;

        if (currentFile) {
          // formData.append("media", currentFile.file); // File object
          formData.append("file_name", currentFile.name);
          formData.append("mime_type", currentFile.type);

          const mediaFileType = currentFile.type.split("/")[0]; // 'audio', 'image', or 'video'
          let mediaUrlForPayload = currentFile.url;
          if (mediaUrlForPayload && !mediaUrlForPayload.startsWith('gs://')) {
            // This should ideally not happen if the above validation is in place,
            // but as a double-check:
            console.error("CRITICAL: Attempting to use non-GCS URL in mediaUrls:", mediaUrlForPayload);
            // Handle error or default to null/empty to prevent sending bad data
            mediaUrlForPayload = null; // Or an empty string, depending on backend/CF expectations
          }

          const urlsObject = {
            audios: mediaFileType === "audio" && mediaUrlForPayload ? [mediaUrlForPayload] : [],
            images: mediaFileType === "image" && mediaUrlForPayload ? [mediaUrlForPayload] : [],
            videos: mediaFileType === "video" && mediaUrlForPayload ? [mediaUrlForPayload] : [],
            documents:
              (mediaFileType === "text" || currentFile.type === "application/pdf") && mediaUrlForPayload
                ? [mediaUrlForPayload]
                : [],
          };

          formData.append("mediaUrls", JSON.stringify(urlsObject));
          formData.append("media_url", currentFile.url); // This is for storeUserMessage, may also need validation if used elsewhere directly

          // const fileType = modalMapping[modalContent]?.messageType || "text";
          // switch (fileType) {
          //   case "image":
          //     key = "image_bytes";
          //     break;
          //   case "audio":
          //     key = "audio_bytes";
          //     break;
          //   case "video":
          //     key = "video_bytes";
          //     break;
          //   default:
          //     key = "file_bytes";
          // }

          // const reader = new FileReader();
          // reader.readAsDataURL(currentFile.file); // Convert to Base64
          // reader.onloadend = async () => {
          //   fileContent = reader.result.split(",")[1]; // Extract the Base64 part

          //   // Append the file content to the formData
          //   formData.append(key, fileContent);
          //   // console.log(fileContent);
          // };
        }
        formData.append("message_type", key || "text");

        const result = await apiService.storeUserMessage(formData);
        if (result.success) {
          setToastMessage(result.success);
          setToastType("success");

          let image_bytes = null;
          let audio_bytes = null;
          let video_bytes = null;

          if (key === "image_bytes") {
            image_bytes = fileContent;
          } else if (key === "audio_bytes") {
            audio_bytes = fileContent;
          } else if (key === "video_bytes") {
            video_bytes = fileContent;
          }

          const userInput = {
            image_bytes,
            audio_bytes,
            video_bytes,
          };

          formData.append("message_id", result.message_id);
          formData.append("userInput", userInput);
          formData.append("sentAt", new Date().toISOString());
          formData.append("prompt", prompt);

          // const newMessage = {
          //   id: result.message_id,
          //   sender: "User",
          //   prompt: textPrompt,
          //   text: textPrompt,
          //   image_bytes: fileContent,
          //   audio_bytes: null,
          //   video_bytes: null,
          //   avatar: "https://via.placeholder.com/50",
          //   file: {
          //     name: file.name,
          //     type: file.type,
          //   },
          //   sentAt: new Date().toISOString(),
          // };

          // console.log(newMessage);

          updateLocalState("newMessage", formData);
          // console.log(formData);

          if (chatPage) {
            console.log("[CustomInput handleGenerate] Clearing chatInputRequiresTrackSelectionForId after successful message submission.");
            updateLocalState("chatInputRequiresTrackSelectionForId", null);
          }

          if (createProject) {
            navigate(`/chatPage?projectId=${projectId}`);
            posthog.capture("New Project created successfully", {
              is_user_logged_in: true
            });
          }
        } else {
          posthog.capture("Failed to create New Project", {
            is_user_logged_in: true
          });
          setToastMessage(result.error || "An error occurred");
          setToastType("error");
        }

        // else {
        //   const formData = new FormData();
        //   formData.append("project_id", projectId);
        //   formData.append("user_id", userId);
        //   formData.append("content", textPrompt);
        //   formData.append("message_type", "text");

        //   const result = await apiService.storeUserMessage(formData);
        //   if (result.success) {
        //     setToastMessage(result.success);
        //     setToastType("success");

        //     const newMessage = {
        //       id: result.message_id,
        //       sender: "User",
        //       prompt: textPrompt,
        //       text: textPrompt,
        //       image_bytes: null,
        //       audio_bytes: null,
        //       video_bytes: null,
        //       avatar: "https://via.placeholder.com/50",
        //       file: null,
        //       sentAt: new Date().toISOString(),
        //     };

        //     updateLocalState("newMessage", newMessage);

        //     if (createProject) {
        //       navigate(`/chatPage?projectId=${projectId}`);
        //     }
        //   } else {
        //     setToastMessage(result.error || "An error occurred");
        //     setToastType("error");
        //   }
        // }
      } catch (error) {
        console.error("Error storing user message:", error);
        Sentry.captureException(error);
        setToastMessage("An error occurred while saving");
        setToastType("error");
      } finally {
        if (closeModal) {
          closeModal();
        }
        updateLocalState("textPrompt", "");
        updateLocalState("file", null);

        setTextPrompt("");
        setFile(null);
        setCharCount(0);
      }
    }
  };

  const modalMapping = {
    Audio: {
      title: "Attach Audio",
      fileTypes: [".mp3", ".aac", ".m4a", ".wav"],
      messageType: "audio",
    },
    Image: {
      title: "Attach Image",
      fileTypes: [".jpg", ".png"],
      messageType: "image",
    },
    Video: {
      title: "Attach Video",
      fileTypes: [".mp4", ".avi", ".mov"],
      messageType: "video",
    },
    Text: {
      title: "Attach Text",
      fileTypes: [".txt", ".pdf"],
      messageType: "text",
    },
  };

  const getModalKey = (option) => {
    // Strip "Upload " prefix if present
    return option.replace("Upload ", "");
  };

  useEffect(() => {
    if (textareaRef.current && chatPage) {
      // Reset height to auto first to handle text removal
      textareaRef.current.style.height = "auto";

      // Calculate required height including the suggestion if shown
      const suggestedHeight =
        showSuggestion && localState.nextPromptSuggestion && !textPrompt
          ? Math.min(
            getTextAreaHeight(24),
            maxHeight
          )
          : Math.min(textareaRef.current.scrollHeight, maxHeight);

      // Set the new height
      textareaRef.current.style.height = `${suggestedHeight}px`;

      updateLocalState("textInputHeight", textareaRef.current.scrollHeight);

      // Set overflow if content exceeds maxHeight
      textareaRef.current.style.overflow =
        suggestedHeight >= maxHeight ? "auto" : "hidden";
    }
  }, [textPrompt, showSuggestion, localState.nextPromptSuggestion]);

  // Helper function to calculate height for a given text
  const getTextAreaHeight = (text) => {
    const textarea = textareaRef.current;
    textarea.style.height = "auto";
    textarea.value = text;
    const height = textarea.scrollHeight;
    textarea.value = textPrompt; // Reset to actual value
    return height;
  };

  // <<< ADDED Effect to listen for file from modal >>>
  useEffect(() => {
    if (localState.uploadedFileFromModal) {
      console.log("CustomInput detected file from modal:", localState.uploadedFileFromModal);
      const fileFromModal = localState.uploadedFileFromModal;

      // --- MODIFIED CONDITION START ---
      // Duck-typing for File-like object: check for name, type, size and the slice method (characteristic of Blobs/Files)
      const isLikelyFileObject = fileFromModal.file &&
        typeof fileFromModal.file.name === 'string' &&
        typeof fileFromModal.file.type === 'string' &&
        typeof fileFromModal.file.size === 'number' &&
        typeof fileFromModal.file.slice === 'function';

      if (fileFromModal.url && !fileFromModal.url.startsWith('gs://') && isLikelyFileObject) {
        // --- MODIFIED CONDITION END ---
        console.log("Local URL detected in file from modal, attempting to upload to GCS:", fileFromModal.url);
        setIsUploading(true); // Show loading/uploading indicator

        const formData = new FormData();
        // Make sure to use fileFromModal.file, which should be the actual File/Blob
        formData.append("file", fileFromModal.file);
        formData.append("folder", "user-uploads");

        fetch(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/upload`,
          {
            method: "POST",
            body: formData,
            // headers: { Authorization: `Bearer ${getCookie("token")}` }, // If auth is needed
          }
        )
          .then(response => {
            if (!response.ok) {
              return response.json().then(errData => {
                throw new Error(errData.error || `Failed to upload file: ${response.statusText}`);
              }).catch(() => {
                const Error = new Error(`Failed to upload file: ${response.statusText}`);
                Sentry.captureException(Error);
                throw Error;
              });
            }
            return response.json();
          })
          .then(data => {
            if (data.success && data.url && data.url.startsWith('gs://')) {
              console.log("Successfully uploaded modal file to GCS. New GCS URL:", data.url);
              const updatedFileObject = {
                name: fileFromModal.name,
                type: fileFromModal.type,
                file: fileFromModal.file,
                url: data.url,
              };
              setFile(updatedFileObject);
              posthog.capture("Modal file automatically uploaded to GCS", {
                is_user_logged_in: true
              });
            } else {
              console.error("Failed to upload file from modal to GCS or received invalid URL:", data.error || data.url);
              setToastMessage(data.error || "Failed to process uploaded file. Please check logs.");
              setToastType("error");
              setFile(null);
            }
          })
          .catch(error => {
            console.error("Error uploading file from modal to GCS:", error);
            Sentry.captureException(error); 
            setToastMessage(`Error processing uploaded file: ${error.message}`);
            setToastType("error");
            setFile(null);
          })
          .finally(() => {
            setIsUploading(false);
            updateLocalState('uploadedFileFromModal', null);
          });

      } else if (fileFromModal.url && fileFromModal.url.startsWith('gs://')) {
        console.log("Modal file already has a GCS URI:", fileFromModal.url);
        setFile(fileFromModal);
        updateLocalState('uploadedFileFromModal', null);
      } else if (fileFromModal.url && !fileFromModal.url.startsWith('gs://') && !isLikelyFileObject) {
        console.error("Local URL detected for modal file, but 'file' property is not a valid File-like object:", fileFromModal);
        setToastMessage("Error: Cannot process the provided file. Please select the file again.");
        setToastType("error");
        setFile(null);
        updateLocalState('uploadedFileFromModal', null);
      } else {
        console.log("Modal file does not meet conditions for GCS upload or already processed, using as is (or clearing if invalid):", fileFromModal);
        setFile(fileFromModal.url ? fileFromModal : null);
        updateLocalState('uploadedFileFromModal', null);
      }
    }
  }, [localState.uploadedFileFromModal, updateLocalState, posthog]);

  // Determine the placeholder text
  let placeholderTextToShow = fromDiscoverPage
    ? "What would you like to customize?"
    : "What music would you like to create?";

  if (chatPage) {
    if (localState.responseInProgress) { // If a response is in progress on chat page
      placeholderTextToShow = "Give these tracks a good listen 👂";
    } else if (isDisabledByTrackSelectionRequirement) { // Else, if input is disabled due to track selection requirement
      placeholderTextToShow = "Please select ✅ a track to continue wubbling...";
    } else if (!textPrompt && showSuggestion && localState.nextPromptSuggestion) {
      placeholderTextToShow = "What would you like to change?";
    } else {
      placeholderTextToShow = "What would you like to change?";
    }
  }

  // Add useEffect for handling Escape key press
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        // If closeModal prop is provided (meaning it's used as a modal), call it.
        if (closeModal) {
          closeModal();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeModal]); // Add closeModal to the dependency array

  useEffect(() => {
    if (textareaRef.current) {
      setTextareaHeight(textareaRef.current.offsetHeight);
    }
  }, [textPrompt]); // Also add dependencies if textarea can resize for other reasons

  return (
    <div
      id={id}
      ref={dropRef}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`self-center relative flex flex-col items-center justify-center gap-0 ${className} ${inChatPage ? "rounded-3xl p-2" : "rounded-2xl"
        }${onRedo ? " bg-[#2C2A2C] p-2 rounded-3xl" : ""}`}
    >
      {onRedo && (
        <div className="flex w-full justify-between items-center pb-2 pl-2 pr-1 rounded-xl">
          <p className="text-sm text-start font-extralight ">
            Here is the result based on what you have selected
          </p>
          <button
            onClick={onRedo}
            className="flex items-center gap-1 ml-2 h-7 px-2 text-sm rounded-full bg-white/5 hover:bg-white/10 text-[#878188] hover:text-white transition-colors"
          >
            <ArrowRotateRight className="w-3.5 h-3.5" />
            Redo
          </button>
        </div>
      )}
      {isDragging && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div className="h-[90%] w-[95%] border border-[color:var(--Color-Light-Opacity-White-30,rgba(255,255,255,0.30))] rounded-[20px] border-dashed p-8 flex flex-col items-center justify-center gap-2 z-10">
            <div className="flex flex-col items-center justify-center">
              <img src="drag-drop.svg" alt="Drag and Drop" />
            </div>
            <span className="text-2xl font-medium text-white">Drop it!</span>
            <span className="text-sm text-[#878188] text-center">
              Supported formats include common text, image, audio, and video files.
            </span>
            <span className="text-sm text-[#878188]">
              Limit: 1 file, up to 20 MB
            </span>
          </div>
        </div>
      )}
      <div
        ref={mainInputBoxRef}
        className={`self-stretch flex flex-col items-start justify-center bg-[#1d1b1d] border border-solid border-[#3c393c] w-full relative rounded-3xl p-2`}
      >
        <div className="self-stretch rounded-xl flex flex-row my-1 items-start justify-start">
          <div className={`flex relative w-full`}>
            {!chatPage && !fromDiscoverPage && (
              <div
                className="relative z-10"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleDiceClick}
                id="dice-icon"
              >
                <img
                  src="/Randomize.svg"
                  alt="Randomize"
                  className={`ml-1.5 mt-0.5 transition duration-300 origin-center cursor-pointer`}
                  style={{
                    filter: `${isHovered || diceIsClicked
                      ? `brightness(2) grayscale(0.8) contrast(1.2) saturate(1.2)`
                      : `brightness(0.8) grayscale(0.8) contrast(1.2) saturate(1.2)`
                      }`,
                    transform: `${isHovered || diceIsClicked
                      ? "rotate(45deg)"
                      : "rotate(0deg)"
                      }`,
                    transition: "filter 0.3 ease, transform 0.3s ease",
                  }}
                />
                {isHovered && (
                  <div className="absolute bg-white/10 backdrop-blur-md text-white text-xs p-1 pl-1.5 rounded top-7 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    Randomize
                  </div>
                )}
              </div>
            )}
            <textarea
              id="text-input-field"
              type="text"
              className={`relative text-base font-extralight w-full bg-transparent px-2 ${fromDiscoverPage ? `mb-6` : `mb-4`
                } border-none ${isInputDisabled ? "cursor-not-allowed" : "cursor-text"} focus:outline-none resize-none appearance-none focus:ring-0 overflow-hidden scrollable-content`}
              value={textPrompt}
              placeholder={placeholderTextToShow}
              onChange={handleChange}
              onKeyDown={handleTextareaKeyDown}
              onClick={() => {
                posthog.capture("Click-Plus-CustomInputBox", {
                  is_user_logged_in: true
                });
                if (shouldShowAutocomplete && textPrompt.trim().length > 0 && suggestions.length > 0) {
                  setShowSuggestionsContainer(true); // Re-show if clicking back into textarea with suggestions available
                }
              }}
              rows={1}
              ref={textareaRef}
              style={{ maxHeight: `${maxHeight}px` }}
              maxLength={MAX_CHARS}
              disabled={isInputDisabled}
            />
          </div>
        </div>

        <div className="self-stretch flex flex-row items-end justify-between text-sm text-color-grey-40">
          <div className="flex flex-row items-center justify-start relative">
            {!file && !isUploading && !fromDiscoverPage && !chatPage ? (<Dropdown
              options={[
                {
                  label: "Audio",
                  icon: "MusicNote",
                  id: "audio-option",
                },
                {
                  label: "Image",
                  icon: "Picture",
                  id: "image-option",
                },
                {
                  label: "Video",
                  icon: "Filmstrip",
                  id: "video-option",
                },
                { label: "Document", icon: "File", id: "text-option" },
              ]}
              onOptionSelect={handleOptionSelect}
              className={`p-1 w-32 mb-1 font-medium text-sm text-[#878188] bg-[#100f10] border border-solid border-[#2c2a2c] rounded-md z-10 ${dropdownStyle}`}
              iconOnly={true}
              disabled={isInputDisabled || localState.responseInProgress}
            />
            ) : (
              !file && !isUploading && !fromDiscoverPage && (<Dropdown
                options={[
                  {
                    label: "Attach",
                    icon: "Paperclip",
                    id: "attach-option"
                  },
                  {
                    label: (
                      <div className="flex items-center justify-between w-full">
                        <span>Extend</span>
                        <div
                          className="relative inline-block ml-1"
                          onMouseEnter={() => setShowExtendTooltip(true)}
                          onMouseLeave={() => setShowExtendTooltip(false)}
                        >
                          <CircleInfo className="w-3.5 h-3.5 text-[#878188]" />
                          {showExtendTooltip && (
                            <div
                              className="absolute left-1/2 -translate-x-1/2 top-6 bg-[#2C2A2C] text-white text-xs py-1 px-2 rounded whitespace-nowrap z-50"
                            >
                              3 mins max!
                            </div>
                          )}
                        </div>
                      </div>
                    ),
                    icon: "Stopwatch",
                    id: "extend-option"
                  },
                  {
                    label: "Edit",
                    icon: "PencilToLine",
                    id: "edit-option"
                  },
                ]}
                onOptionSelect={handleOptionSelect}
                className={`p-1 w-32 mb-1 font-medium text-sm text-[#878188] bg-[#100f10] border border-solid border-[#2c2a2c] rounded-md z-10 ${dropdownStyle}`}
                iconOnly={true}
                disabled={isInputDisabled || localState.responseInProgress}
              />
              )
            )
            }
            {isUploading && (
              <div className="flex items-center h-8 px-2">
                <div className="w-4 h-4 border-2 border-t-2 border-grey-300 border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-2 text-[#878188] ">Uploading...</span>
              </div>
            )}
          </div>
          <div className="flex items-center">
            <CircularProgress value={charCount} maxChars={MAX_CHARS} />
            {localState?.responseInProgress && chatPage ? (
              <div
                className="w-8 h-8 flex items-center justify-center bg-white rounded-full cursor-pointer"
                onClick={handleStopGeneration}
              >
                <StopFill className="w-4 h-4 text-[#100F10]" />
              </div>
            ) : (
              <>
                <button
                  id="generate-button-2"
                  title={disableGenerate ? "Too long" : (isDisabledByTrackSelectionRequirement ? "Select a track first" : (localState.responseInProgress ? "Generation in progress" : ""))}
                  disabled={
                    isInputDisabled ||
                    isUploading ||
                    !((textPrompt && textPrompt.trim()) || file) ||
                    disableGenerate
                  }
                  onClick={() => {
                    handleGenerate()
                    posthog.capture("Click-Generate-CustomInputBox", {
                      is_user_logged_in: true
                    })
                  }}
                  className={`w-8 h-8 flex flex-row items-center justify-center py-0 px-2 rounded-full ${((textPrompt && textPrompt.trim()) || file) && !disableGenerate && !isInputDisabled
                    ? "bg-gradient-to-r from-[#fc10f2] to-[#3078e4] hover:from-[#e509e4] hover:to-[#256bb8]"
                    : "text-[#878188] bg-[#4E4A4F] cursor-not-allowed"
                    }`}
                >
                  <img
                    className="w-4 relative h-4 overflow-hidden shrink-0"
                    alt=""
                    src={
                      (textPrompt && textPrompt.trim() && !disableGenerate) ||
                        (file && !disableGenerate)
                        ? "/MusicGenerateEnabled.svg"
                        : "/MusicGenerateDisabled.svg"
                    }
                  />
                </button>
              </>)}
          </div>
        </div>

        {file && !isUploading && (
          <div className="absolute bottom-2 left-2 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-full pl-0 pr-2.5 h-8">
            <div className="mr-2">
              {file.type.startsWith('image/') && file.url ? (
                <img src={file.url} alt={file.name} className="w-6 h-6 ml-1 rounded-full object-cover" />
              ) : fromDiscoverPage && imageSrc ? (
                <img src={imageSrc} className="w-6 h-6 ml-1 rounded-full" />
              ) : (
                <File className="w-4 h-4 ml-2.5 text-white" />
              )}
            </div>
            <span className="text-sm font-light text-white mr-2">
              {truncateFileName(file.name, 15)}
            </span>
            <button
              onClick={handleFileRemove}
              className="text-[#878188] hover:text-white"
            >
              <Xmark className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {shouldShowAutocomplete && showSuggestionsContainer && (
        <div
          ref={suggestionsContainerRef}
          className="absolute left-0 right-0 flex flex-col z-20 px-2 max-h-[16vh] sm:max-h-[20vh] md:max-h-[25vh] overflow-y-auto" 
          style={{
            top: `${textareaRef.current.offsetHeight + 80}px`
          }}
        >
          {suggestions.map((suggestion, index) => {
            // Find the exact matching suggestion
            const isExactMatch = suggestion.toLowerCase().startsWith(textPrompt.toLowerCase());
            const normalPart = isExactMatch ? textPrompt : suggestion.substring(0, textPrompt.length);
            const boldPart = isExactMatch ? suggestion.substring(textPrompt.length) : suggestion.substring(textPrompt.length);
            
            return (
              <div
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setActiveSuggestionIndex(index)}
                className={`flex-1 whitespace-wrap w-full break-words flex items-center h-fit flex-shrink-0 min-h-10 py-2 px-2 gap-0.5 text-left text-[#C5C3C6] text-sm
                            ${isFetchingSuggestions ? 'opacity-50' : 'opacity-100'}
                            ${index === activeSuggestionIndex
                    ? "bg-white/10 rounded-lg"
                    : `${index !== suggestions.length - 1 ? "border-b border-white/10" : ""} hover:bg-white/10 hover:border-opacity-0 font-extralight`
                  }`}
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  animation: isFetchingSuggestions ? 'none' : 'fadeInSlideUp 0.3s ease-out forwards'
                }}
              >
                <p className="h-fit whitespace-pre-wrap break-words leading-snug">
                  <span className={`${index === activeSuggestionIndex ? 'font-extralight text-[#C5C3C6]' : ''}`}>{normalPart}</span>
                  <span className="font-normal text-[#E0DFE1]"> {boldPart}</span>
                </p>
              </div>
            );
          })}
          {isFetchingSuggestions && suggestions.length > 0 && (
            <div className="absolute inset-0 bg-black/5 backdrop-blur-[2px] rounded-lg" />
          )}
        </div>
      )}

      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage("")}
        />
      )}
      {triggerTaskModal && (<TaskCompletion trigger={triggerTaskModal} />)}
    </div>
  );
};

export default CustomInput;